import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ClassCard = ({ classData, onBook, onCancel, onJoinWaitlist, onViewDetails }) => {
  const {
    id,
    name,
    instructor,
    instructorImage,
    instructorImageAlt,
    time,
    duration,
    capacity,
    booked,
    difficulty,
    type,
    equipment,
    rating,
    isBooked,
    isWaitlisted,
    isFull,
    isMemberOnly,
    waitlistPosition
  } = classData;

  const availableSpots = capacity - booked;
  const capacityPercentage = (booked / capacity) * 100;

  const getDifficultyColor = (level) => {
    const colors = {
      Beginner: 'bg-success/10 text-success',
      Intermediate: 'bg-warning/10 text-warning',
      Advanced: 'bg-error/10 text-error'
    };
    return colors?.[level] || colors?.Beginner;
  };

  const getCapacityColor = () => {
    if (capacityPercentage >= 90) return 'bg-error';
    if (capacityPercentage >= 70) return 'bg-warning';
    return 'bg-success';
  };

  return (
    <div className="card hover:shadow-xl transition-all duration-250">
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        <div className="flex-shrink-0">
          <div className="w-full md:w-24 h-48 md:h-24 rounded-lg overflow-hidden">
            <Image
              src={instructorImage}
              alt={instructorImageAlt}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground truncate">
                  {name}
                </h3>
                {isMemberOnly && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium whitespace-nowrap">
                    <Icon name="Lock" size={12} className="mr-1" />
                    Members Only
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Icon name="User" size={16} />
                <span className="truncate">{instructor}</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="Clock" size={16} />
                  <span className="whitespace-nowrap">{time}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Timer" size={16} />
                  <span className="whitespace-nowrap">{duration} min</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={16} color="var(--color-warning)" />
                  <span className="whitespace-nowrap">{rating}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(difficulty)}`}>
                {difficulty}
              </span>
              <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-muted text-foreground text-xs font-medium">
                {type}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            {equipment?.map((item, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded bg-muted text-muted-foreground text-xs"
              >
                <Icon name="Dumbbell" size={12} className="mr-1" />
                {item}
              </span>
            ))}
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">
                {isFull ? 'Class Full' : `${availableSpots} spots left`}
              </span>
              <span className="text-foreground font-medium">
                {booked}/{capacity}
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${getCapacityColor()} transition-all duration-250`}
                style={{ width: `${capacityPercentage}%` }}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {isBooked ? (
              <>
                <Button
                  variant="outline"
                  className="flex-1"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Booked
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => onCancel(id)}
                  iconName="X"
                  iconPosition="left"
                >
                  Cancel
                </Button>
              </>
            ) : isWaitlisted ? (
              <Button
                variant="outline"
                className="flex-1"
                iconName="Clock"
                iconPosition="left"
              >
                Waitlist #{waitlistPosition}
              </Button>
            ) : isFull ? (
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => onJoinWaitlist(id)}
                iconName="UserPlus"
                iconPosition="left"
              >
                Join Waitlist
              </Button>
            ) : (
              <Button
                variant="default"
                className="flex-1"
                onClick={() => onBook(id)}
                iconName="Calendar"
                iconPosition="left"
              >
                Book Class
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => onViewDetails(id)}
              iconName="Info"
              iconPosition="left"
            >
              Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassCard;